<template>
  <div class="d-inline-flex justify-content-between w-100 pr-3 pl-3 p-1">
    <span class="pt-1">
      <a href="#" v-on:click="selectPlatform()" class="mr-1 text-decoration-none">
        <font-awesome-icon :icon="['fas', 'circle']" :style="{ color: platform.color }" class="mr-2" />
        <strong><span :class="platformTitleColor">{{ platform.description }}</span></strong>
      </a>
      <a v-if="platform.info_url !== '' && full" :href="platform.info_url" target="_blank" :title="'Open more information on ' + platform.description" class="text-lightgrey">
        <font-awesome-icon :icon="['fas', 'external-link-alt']" size="xs" class="align-baseline ml-1 mr-1" />
      </a>
      <span v-if="platform.type === 'auv-g500'" class="mr-1">
        <b-badge v-if="platform_diving === 1" class="badge badge-success align-text-top ml-1">Diving</b-badge>
        <b-badge v-else-if="platform_diving === 0" class="badge badge-warning align-text-top ml-1">Surface</b-badge>
        <b-badge v-if="platform_captain === 0 && full" class="badge badge-muted align-text-top ml-1">Idle</b-badge>
        <b-badge v-else-if="platform_captain === 1 && full" class="badge badge-info align-text-top ml-1">Goto</b-badge>
        <b-badge v-else-if="platform_captain === 2 && full" class="badge badge-info align-text-top ml-1">Mission {{ platform_step }}/{{ platform_total_steps }}</b-badge>
        <b-badge v-else-if="platform_captain === 3 && full" class="badge badge-warning align-text-top ml-1">Keep</b-badge>
        <b-badge v-else-if="platform_captain === 4 && full" class="badge badge-warning align-text-top ml-1">Safety</b-badge>
      </span>
      <span v-if="platform.type === 'transponder'">
        <b-badge v-if="platform_stage === 'SURFACE'" class="badge badge-warning align-text-top ml-1">Surface</b-badge>
        <b-badge v-else-if="platform_stage === 'DESCENT'" class="badge badge-info align-text-top ml-1">Descent</b-badge>
        <b-badge v-else-if="platform_stage === 'SEAFLOOR'" class="badge badge-success align-text-top ml-1">Seafloor</b-badge>
        <b-badge v-else-if="platform_stage === 'ASCENT'" class="badge badge-info align-text-top ml-1">Ascent</b-badge>
        <span v-if="'last_change' in platform">
          <b-badge v-if="platform_position_source === 'NOT_SET' && full" class="badge badge-danger align-text-top ml-1">No position</b-badge>
          <b-badge v-else-if="full" class="badge badge-success align-text-top ml-1">Position</b-badge>
        </span>
      </span>
      <span v-else>
        <b-badge v-if="platform.type.startsWith('ship')" class="badge badge-gray align-text-top ml-1">Ship</b-badge>
        <b-badge v-if="platform.type.startsWith('drifter')" class="badge badge-gray align-text-top ml-1">Drifter</b-badge>
        <b-badge v-if="platform.type.startsWith('glider')" class="badge badge-gray align-text-top ml-1">Glider</b-badge>
        <b-badge v-if="platform.type.startsWith('argo-float')" class="badge badge-gray align-text-top ml-1">Argo Float</b-badge>
        <b-badge v-if="platform.type.startsWith('buoy')" class="badge badge-gray align-text-top ml-1">Buoy</b-badge>
        <b-badge v-if="platform.type.startsWith('mooring')" class="badge badge-gray align-text-top ml-1">Mooring</b-badge>
        <b-badge v-if="platform.type.startsWith('waveglider')" class="badge badge-gray align-text-top ml-1">Wave Glider</b-badge>
        <b-badge v-if="platform.type.startsWith('saildrone')" class="badge badge-gray align-text-top ml-1">Saildrone</b-badge>
        <b-badge v-if="platform.type.startsWith('sailbuoy')" class="badge badge-gray align-text-top ml-1">Sailbuoy</b-badge>
        <b-badge v-if="platform.type.startsWith('sonde')" class="badge badge-gray align-text-top ml-1">Sonde</b-badge>
        <!-- TODO logger als neuen Typen aufnehmen (SOOP) oder mit Sonden zusammenfassen? -->
        <b-badge v-if="platform.type.startsWith('logger')" class="badge badge-gray align-text-top ml-1">Logger</b-badge>
      </span>
    </span>
    <span style="text-align: right;" class="mt-1">
      <span v-if="platform.type === 'auv-g500' && full" class="d-none d-xl-inline">
        <b-badge class="badge align-text-top" v-for="channel in channels" :key="channel.id" variant="light" :class="channel_active_class(channel.id, platform.id)">
          {{ capitalizeFirstLetter(channel.type) }} <font-awesome-icon :icon="['fas', 'circle']" size="xs" :class="channel_busy_class(channel.id, platform.id)" />
        </b-badge>
      </span>
      <div style="display: inline-block; width: 130px;">
        <font-awesome-icon :icon="['fas', 'history']" /> <span ref="last_change" v-b-popover.hover.left="platform_lastchange" :data-timestamp="platform.last_change">- No data -</span>
      </div>
    </span>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapGetters, mapMutations } from 'vuex'
import Helpers from '@/mixins/helpers'

const UPDATE_INTERVAL_MS = 1000

export default {
  name: 'MainWindow',
  mixins: [
    Helpers
  ],
  components: {},
  props: [
    'platformId',
    'full'
  ],
  mounted: function () {
    // Initialize last change timestamp
    this.updateLastUpdate()
    // Start last change timestamp updater
    this.updateTask = setInterval(this.updateLastUpdate, UPDATE_INTERVAL_MS)
  },
  beforeDestroy: function () {
    // Remove background job again
    clearInterval(this.updateTask)
  },
  methods: {
    ...mapMutations(['select_platform']),
    getAge: function (timestamp, skip) {
      if (timestamp > 0) {
        return moment.unix(timestamp).fromNow(skip)
      } else {
        return 'never'
      }
    },
    selectPlatform: function () {
      if (this.selectedPlatform !== this.platformId) {
        // Select platform globally
        this.select_platform(this.platformId)
      } else {
        // Deselect all platforms
        this.select_platform(0)
      }
    },
    updateLastUpdate: function () {
      // if (this.full) {
      const timestamp = Number(this.$refs.last_change.getAttribute('data-timestamp'))
      if (timestamp > 0) {
        this.$refs.last_change.innerText = this.getAge(timestamp, false)
      }
      // }
    }
  },
  computed: {
    ...mapGetters(['lastUpdate', 'currentUpdate']),
    ...mapState(['platforms', 'channels', 'selectedPlatform']),
    platform: function () {
      return this.platforms[this.platformId]
    },
    platformTitleColor: function () {
      const timestamp = this.platform.last_change
      if (timestamp > 0) {
        return ''
      } else {
        return 'text-muted'
      }
    },
    channel_active_class: function () {
      // TODO: Signal error
      return (channelId, platformId) => {
        platformId = Number(platformId)
        if (platformId in this.channels[channelId].active) {
          if (this.channels[channelId].active[platformId]) {
            return 'text-primary'
          }
        }
        return 'text-muted'
      }
    },
    channel_busy_class: function () {
      return (channelId, platformId) => {
        platformId = Number(platformId)
        if (Number(platformId) in this.channels[channelId].active &&
          this.channels[channelId].active[Number(platformId)]) {
          if (platformId in this.channels[channelId].busy) {
            if (this.channels[channelId].busy[platformId]) {
              return 'text-danger'
            } else {
              return 'text-success'
            }
          }
        }
        return 'text-muted'
      }
    },
    platform_stage: function () {
      const update = this.currentUpdate(this.platformId, 4, 1)
      return (update.length !== 0) ? update[1] : 'UNKNOWN'
    },
    platform_position_source: function () {
      const update = this.currentUpdate(this.platformId, 1, 8)
      return (update.length !== 0) ? update[1] : 'NOT_SET'
    },
    platform_diving: function () {
      const update = this.currentUpdate(this.platformId, 1, 4)
      if (update.length !== 0) {
        if (Number(update[1]) > 1.0) {
          return 1
        } else {
          return 0
        }
      }
      return -1
    },
    platform_captain: function () {
      const update = this.currentUpdate(this.platformId, 4, 6)
      return (update.length !== 0) ? Number(update[1]) : -1
    },
    platform_step: function () {
      const update = this.currentUpdate(this.platformId, 4, 2)
      return (update.length !== 0) ? update[1] : 0
    },
    platform_total_steps: function () {
      const update = this.currentUpdate(this.platformId, 4, 3)
      return (update.length !== 0) ? update[1] : 0
    },
    platform_lastchange: function () {
      return moment.unix(this.platform.last_change).format('MMMM DD YYYY HH:mm')
    }
  }
}

</script>

<style scoped>

.text-lightgrey:link {
  color: rgba(0, 123, 255, 0.4);
}
.text-lightgrey:visited {
  color: rgba(0, 123, 255, 0.4);
}
.text-lightgrey:hover {
  color: rgba(0, 123, 255, 1.0);
}

</style>
